// Shared receipt (scontrino) builder used by:
//  - POS Counter (nuovo scontrino, anteprima + stampa)
//  - Ristampa da /movimenti (admin)
//  - Ristampa da storico cliente (portale pubblico)
//
// La funzione produce un HTML autonomo (senza framework) che può essere
// stampato in una nuova finestra oppure iniettato in un iframe per l'anteprima.

import { resolveImageUrl } from "../context/AppSettingsContext";

function escHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[ch]));
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(0, 10);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch (_) { return String(iso).slice(0, 10); }
}

function couponDiscountLabel(cp) {
  if (!cp) return "";
  const v = Number(cp.discount_value || 0);
  return cp.discount_type === "percent" ? `-${v}%` : `-€ ${v.toFixed(2)}`;
}

/**
 * @param {Object} opts
 * @param {Object} opts.earn - { original_amount, final_amount, coupon:{code,discount_eur}|null, transaction:{items,points_delta,branch_id} }
 * @param {Object} opts.customer - { name, card_number, points }
 * @param {Object|null} opts.branch - { name, address, phone, logo_url, receipt_footer }
 * @param {Object} opts.settings - AppSettings globali (logo_url, primary_color, accent_color, app_name, card_footer)
 * @param {Number} opts.multiplier
 * @param {Array}  opts.activeCoupons - lista coupon attivi del cliente
 * @param {Boolean} [opts.duplicate] - true = scontrino copia (aggiunge intestazione "COPIA")
 * @param {String}  [opts.originalDate] - ISO della transazione originale (per la copia)
 * @param {Boolean} [opts.autoPrint] - se true aggiunge <script>window.print()</script>
 */
export function buildReceiptHtml({
  earn, customer, branch, settings,
  multiplier = 1, activeCoupons = [],
  duplicate = false, originalDate = null,
  autoPrint = false,
}) {
  const chainLogo = resolveImageUrl(settings?.logo_url);
  const branchLogo = resolveImageUrl(branch?.logo_url) || chainLogo;
  const showChainSmall = !!(chainLogo && branch?.logo_url && branchLogo !== chainLogo);
  const primary = settings?.primary_color || "#E5383B";
  const accent = settings?.accent_color || "#F59E0B";
  const now = new Date();

  const items = earn?.transaction?.items || [];
  const itemsHtml = items.length
    ? `<table>${items.map((it) => `<tr><td>${it.qty}× ${escHtml(it.name)}${it.daily_deal ? ` <small>(Prodotto del giorno${it.list_price ? ` · listino € ${Number(it.list_price).toFixed(2)}` : ""})</small>` : ""}</td><td class="r">€ ${Number(it.subtotal ?? (it.qty * it.price)).toFixed(2)}</td></tr>`).join("")}</table>`
    : "";

  const usedCode = earn?.coupon?.code || "";
  const activeList = (activeCoupons || [])
    .filter((cp) => cp.status !== "used")
    .filter((cp) => cp.code !== usedCode)
    .slice(0, 8);
  const activeHtml = activeList.length
    ? `<div class="section">
         <div class="section-title">Coupon ancora attivi</div>
         <table class="coupons">
           ${activeList.map((cp) => `
             <tr>
               <td>
                 <div class="cname">${escHtml(cp.name || cp.code)}</div>
                 <div class="ccode">Codice <b>${escHtml(cp.code)}</b>${cp.valid_until ? ` · scade il ${fmtDate(cp.valid_until)}` : ""}</div>
               </td>
               <td class="r cval">${couponDiscountLabel(cp)}</td>
             </tr>
           `).join("")}
         </table>
       </div>`
    : "";

  const branchName = branch?.name || settings?.app_name || "";
  const branchAddr = branch?.address || "";
  const branchPhone = branch?.phone || "";
  const branchFooter = (branch?.receipt_footer || "").trim();
  const chainFooter = (settings?.card_footer || "Grazie e a presto!").trim();
  const footerText = branchFooter || chainFooter;

  const dupHeader = duplicate
    ? `<div class="dup">COPIA — Originale del ${originalDate ? new Date(originalDate).toLocaleString("it-IT") : ""}</div>`
    : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Scontrino</title>
<style>
  body{font-family:'Helvetica','Arial',sans-serif;padding:16px;color:#111;background:#fff;max-width:340px;margin:auto;}
  .dup{background:#111;color:#fff;text-align:center;font-weight:800;font-size:11px;letter-spacing:.2em;padding:6px 8px;border-radius:6px;margin-bottom:10px;}
  .brand{text-align:center;padding-bottom:10px;border-bottom:1px dashed #999;position:relative;}
  .brand .main-logo{width:72px;height:72px;object-fit:cover;border-radius:12px;}
  .brand .chain-logo{position:absolute;top:0;right:0;width:28px;height:28px;object-fit:cover;border-radius:6px;opacity:.85;border:1px solid #ddd;background:#fff;padding:2px;}
  h1{font-size:15px;letter-spacing:.08em;text-transform:uppercase;margin:6px 0 2px;}
  .muted{color:#666;font-size:11px;line-height:1.35;}
  .cust{margin-top:10px;padding:6px 0;border-bottom:1px dashed #ccc;}
  table{width:100%;font-size:13px;margin:8px 0;border-collapse:collapse;}
  td{padding:3px 0;vertical-align:top;}
  td.r{text-align:right;font-family:monospace;}
  .tot{font-weight:800;font-size:16px;border-top:1px solid #111;padding-top:6px;color:${primary};}
  .badge{display:inline-block;background:${accent};color:#000;padding:6px 10px;border-radius:8px;font-weight:800;font-size:14px;margin:8px 0;}
  .section{margin-top:12px;padding-top:8px;border-top:1px dashed #999;}
  .section-title{font-weight:800;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:${primary};margin-bottom:4px;}
  table.coupons td{padding:5px 0;border-bottom:1px dotted #ddd;font-size:12px;}
  table.coupons tr:last-child td{border-bottom:none;}
  .cname{font-weight:700;color:#111;}
  .ccode{font-size:10px;color:#666;margin-top:1px;}
  .cval{font-weight:800;color:${primary};white-space:nowrap;padding-left:6px;}
  .foot{border-top:1px dashed #999;padding-top:8px;margin-top:10px;font-size:10px;color:#666;text-align:center;line-height:1.4;white-space:pre-line;}
  @media print { body{padding:0;} @page { margin: 6mm; } }
</style></head><body>
  ${dupHeader}
  <div class="brand">
    ${showChainSmall ? `<img class="chain-logo" src="${chainLogo}" alt="catena" />` : ""}
    ${branchLogo ? `<img class="main-logo" src="${branchLogo}" alt="logo" />` : ""}
    <h1>${escHtml(branchName)}</h1>
    ${branchAddr ? `<div class="muted">${escHtml(branchAddr)}</div>` : ""}
    ${branchPhone ? `<div class="muted">Tel. ${escHtml(branchPhone)}</div>` : ""}
    <div class="muted" style="margin-top:4px;">${now.toLocaleString("it-IT")}</div>
  </div>
  <div class="cust">
    <div style="font-weight:700;">${escHtml(customer?.name || "")}</div>
    <div class="muted">Carta ${escHtml(customer?.card_number || "")}</div>
  </div>
  ${itemsHtml}
  <table>
    <tr><td>Importo</td><td class="r">€ ${Number(earn?.original_amount || 0).toFixed(2)}</td></tr>
    ${earn?.coupon ? `<tr style="color:${primary};"><td>Coupon ${escHtml(earn.coupon.code)}</td><td class="r">-€ ${Number(earn.coupon.discount_eur).toFixed(2)}</td></tr>` : ""}
    ${earn?.cashback?.used > 0 ? `<tr style="color:${primary};"><td>Credito cashback usato</td><td class="r">-€ ${Number(earn.cashback.used).toFixed(2)}</td></tr>` : ""}
    ${earn?.cashback?.earned > 0 ? `<tr><td>Cashback maturato</td><td class="r">+€ ${Number(earn.cashback.earned).toFixed(2)} (saldo € ${Number(earn.cashback.balance).toFixed(2)})</td></tr>` : ""}
    <tr class="tot"><td>Totale</td><td class="r">€ ${Number(earn?.final_amount || 0).toFixed(2)}</td></tr>
  </table>
  <div class="badge">+ ${Number(earn?.transaction?.points_delta || 0)} PUNTI ACCREDITATI ${multiplier > 1 ? `(${multiplier}× campagna)` : ""}</div>
  <div class="muted">Nuovo saldo: <b>${Number(customer?.points || 0)}</b> punti totali</div>
  ${activeHtml}
  <div class="foot">${escHtml(footerText)}</div>
  ${autoPrint ? `<script>setTimeout(()=>window.print(), 250);</script>` : ""}
</body></html>`;
}

/**
 * Apre una nuova finestra e stampa lo scontrino.
 */
export function openReceiptPrintWindow(opts) {
  const html = buildReceiptHtml({ ...opts, autoPrint: true });
  const w = window.open("", "_blank", "width=420,height=800");
  if (!w) return false;
  w.document.write(html); w.document.close();
  return true;
}
